import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Folder, ExternalLink, Loader2, RefreshCw, FileImage, FileSpreadsheet, File } from 'lucide-react';
import api from '../../api/auth.api';

interface PlanItem {
  id: string;
  name: string;
  size?: number;
  contentType?: string;
  url?: string;
  lastModified: string;
  isFolder?: boolean;
}

interface SyncStatus {
  synced: boolean;
  fileCount: number;
  lastSync: string | null;
}

// Translation object
const translations = {
  en: {
    plans: 'Plans',
    loading: 'Loading...',
    loadingPlans: 'Loading plans...',
    job: 'Job',
    unableToLoadPlans: 'Unable to Load Plans',
    tryAgain: 'Try Again',
    noPlansAvailable: 'No Plans Synced',
    noFilesInFolder: 'Click Refresh to sync plans from Teams.',
    emptyFolder: 'Empty Folder',
    noFilesHere: 'No files in this folder.',
    failedToOpenFile: 'Failed to open file',
    noJobSelected: 'No job selected',
    plansNotFound: 'Plans folder not found for this job. Please contact the office to set up the job folder.',
    plansNotAvailable: 'Plans are not available at this time. Please try again later.',
    failedToLoadPlans: 'Failed to load plans',
    refresh: 'Refresh',
    syncing: 'Syncing...',
    filesCount: 'files',
    lastSynced: 'Last synced'
  },
  es: {
    plans: 'Planos',
    loading: 'Cargando...',
    loadingPlans: 'Cargando planos...',
    job: 'Trabajo',
    unableToLoadPlans: 'No se Pueden Cargar los Planos',
    tryAgain: 'Intentar de Nuevo',
    noPlansAvailable: 'No Hay Planos Sincronizados',
    noFilesInFolder: 'Toque Actualizar para sincronizar los planos de Teams.',
    emptyFolder: 'Carpeta Vacia',
    noFilesHere: 'No hay archivos en esta carpeta.',
    failedToOpenFile: 'Error al abrir archivo',
    noJobSelected: 'No se ha seleccionado trabajo',
    plansNotFound: 'No se encontro la carpeta de planos para este trabajo. Favor de contactar la oficina para configurar la carpeta del trabajo.',
    plansNotAvailable: 'Los planos no estan disponibles en este momento. Favor de intentar mas tarde.',
    failedToLoadPlans: 'Error al cargar los planos',
    refresh: 'Actualizar',
    syncing: 'Sincronizando...',
    filesCount: 'archivos',
    lastSynced: 'Ultima sincronizacion'
  }
};

export const PlansViewer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  // Get translations
  const t = translations[language];

  // Get job info from navigation state OR query parameters
  const searchParams = new URLSearchParams(location.search);
  const jobName = location.state?.jobName || searchParams.get('jobName');
  const jobNumber = location.state?.jobNumber || searchParams.get('jobNumber');

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    const savedLanguage = localStorage.getItem('language') as 'en' | 'es' | null;
    if (savedLanguage === 'en' || savedLanguage === 'es') {
      setLanguage(savedLanguage);
    }

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    if (!jobName || !jobNumber) {
      setError(t.noJobSelected);
      setLoading(false);
      return;
    }

    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobName, jobNumber]);

  const fetchPlans = async (subfolder: string = '') => {
    try {
      setLoading(true);
      setError(null);

      // Use blob storage API
      const response = await api.get(
        `/api/plans/by-number/${encodeURIComponent(jobNumber)}${subfolder ? `?subfolder=${encodeURIComponent(subfolder)}` : ''}`
      );

      if (response.data.success) {
        // Sort: folders first, then files alphabetically
        const files = response.data.data.files.sort((a: PlanItem, b: PlanItem) => {
          if (a.isFolder && !b.isFolder) return -1;
          if (!a.isFolder && b.isFolder) return 1;
          return a.name.localeCompare(b.name);
        });
        setPlans(files);
        setSyncStatus(response.data.data.syncStatus);
      } else {
        setError(response.data.message || t.failedToLoadPlans);
      }
    } catch (err: any) {
      console.error('Error fetching plans:', err);

      if (err.response?.status === 404) {
        // No plans synced yet - show empty state
        setPlans([]);
        setSyncStatus({ synced: false, fileCount: 0, lastSync: null });
      } else {
        setError(err.response?.data?.message || t.failedToLoadPlans);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);

      // Use longer timeout for sync - it needs to search Teams folders and transfer files
      const response = await api.post('/api/plans/sync-by-number', {
        jobName,
        jobNumber
      }, {
        timeout: 60000 // 60 second timeout for sync operations
      });

      if (response.data.success) {
        // Refresh the file list
        const subfolder = currentPath.length > 0 ? currentPath.join('/') : '';
        await fetchPlans(subfolder);
      } else {
        setError(response.data.message || 'Sync failed');
      }
    } catch (err: any) {
      console.error('Error syncing plans:', err);
      if (err.response?.status === 404) {
        setError(t.plansNotFound);
      } else if (err.code === 'ECONNABORTED') {
        setError('Sync is taking longer than expected. Please try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to sync plans');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleItemClick = async (item: PlanItem) => {
    try {
      if (item.isFolder) {
        // Navigate into folder
        const newPath = [...currentPath, item.name];
        setCurrentPath(newPath);
        fetchPlans(newPath.join('/'));
      } else {
        // Open file directly from blob URL
        if (item.url) {
          window.open(item.url, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (err) {
      console.error('Error opening item:', err);
      alert(t.failedToOpenFile);
    }
  };

  const handleBackToFolder = () => {
    const newPath = currentPath.slice(0, -1);
    setCurrentPath(newPath);
    fetchPlans(newPath.join('/'));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatLastSync = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getFileIcon = (item: PlanItem) => {
    if (item.isFolder) {
      return <Folder size={32} style={{ color: '#f59e0b' }} />;
    }

    const ext = item.name.split('.').pop()?.toLowerCase();

    if (['pdf'].includes(ext || '')) {
      return <FileText size={32} style={{ color: '#ef4444' }} />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'webp'].includes(ext || '')) {
      return <FileImage size={32} style={{ color: '#8b5cf6' }} />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext || '')) {
      return <FileSpreadsheet size={32} style={{ color: '#22c55e' }} />;
    }
    if (['doc', 'docx'].includes(ext || '')) {
      return <FileText size={32} style={{ color: '#3b82f6' }} />;
    }

    return <File size={32} style={{ color: '#64748b' }} />;
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '100vw',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      paddingBottom: '100px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px'
    },
    backButton: {
      padding: '8px',
      backgroundColor: '#e2e8f0',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      color: '#64748b'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1e293b',
      margin: 0,
      flex: 1
    },
    refreshButton: {
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
      fontWeight: 500
    },
    refreshButtonDisabled: {
      backgroundColor: '#94a3b8',
      cursor: 'not-allowed'
    },
    jobInfo: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '8px'
    },
    syncInfo: {
      fontSize: '12px',
      color: '#94a3b8',
      marginBottom: '16px'
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap' as const
    },
    breadcrumbItem: {
      fontSize: '14px',
      color: '#3b82f6',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    breadcrumbSeparator: {
      fontSize: '14px',
      color: '#94a3b8'
    },
    breadcrumbCurrent: {
      fontSize: '14px',
      color: '#64748b'
    },
    loading: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      color: '#64748b'
    },
    error: {
      textAlign: 'center' as const,
      padding: '40px 20px',
      backgroundColor: '#fef2f2',
      borderRadius: '12px',
      border: '1px solid #fecaca'
    },
    errorIcon: {
      fontSize: '48px',
      marginBottom: '16px'
    },
    errorTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#991b1b',
      marginBottom: '8px'
    },
    errorText: {
      fontSize: '14px',
      color: '#dc2626',
      marginBottom: '20px'
    },
    retryButton: {
      padding: '10px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    empty: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    },
    emptyIcon: {
      marginBottom: '16px'
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px'
    },
    emptyText: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '20px'
    },
    list: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s'
    },
    cardDetails: {
      flex: 1,
      minWidth: 0
    },
    cardName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const
    },
    cardMeta: {
      display: 'flex',
      gap: '12px',
      fontSize: '13px',
      color: '#64748b'
    },
    cardAction: {
      color: '#3b82f6',
      flexShrink: 0
    }
  };

  // Loading overlay
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {t.loading}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => {
            if (currentPath.length > 0) {
              handleBackToFolder();
            } else {
              navigate(-1);
            }
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={styles.title}>{t.plans}</h1>
        <button
          style={{
            ...styles.refreshButton,
            ...(syncing ? styles.refreshButtonDisabled : {})
          }}
          onClick={handleSync}
          disabled={syncing}
        >
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} style={syncing ? { animation: 'spin 1s linear infinite' } : {}} />
          {syncing ? t.syncing : t.refresh}
        </button>
      </div>

      {jobName && jobNumber && (
        <div style={styles.jobInfo}>{t.job}: {jobName} {jobNumber}</div>
      )}

      {syncStatus && (
        <div style={styles.syncInfo}>
          {syncStatus.fileCount} {t.filesCount} {syncStatus.lastSync && `• ${formatLastSync(syncStatus.lastSync)}`}
        </div>
      )}

      {/* Breadcrumb navigation */}
      {currentPath.length > 0 && (
        <div style={styles.breadcrumb}>
          <span
            style={styles.breadcrumbItem}
            onClick={() => {
              setCurrentPath([]);
              fetchPlans('');
            }}
          >
            {t.plans}
          </span>
          {currentPath.map((folder, index) => (
            <React.Fragment key={index}>
              <span style={styles.breadcrumbSeparator}>/</span>
              {index === currentPath.length - 1 ? (
                <span style={styles.breadcrumbCurrent}>{folder}</span>
              ) : (
                <span
                  style={styles.breadcrumbItem}
                  onClick={() => {
                    const newPath = currentPath.slice(0, index + 1);
                    setCurrentPath(newPath);
                    fetchPlans(newPath.join('/'));
                  }}
                >
                  {folder}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {error && (
        <div style={styles.error}>
          <div style={styles.errorIcon}>⚠️</div>
          <div style={styles.errorTitle}>{t.unableToLoadPlans}</div>
          <div style={styles.errorText}>{error}</div>
          <button style={styles.retryButton} onClick={() => fetchPlans(currentPath.join('/'))}>
            {t.tryAgain}
          </button>
        </div>
      )}

      {/* Empty State - Not synced yet */}
      {!loading && !error && plans.length === 0 && !syncStatus?.synced && (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            <RefreshCw size={64} style={{ color: '#3b82f6', opacity: 0.7 }} />
          </div>
          <div style={styles.emptyTitle}>{t.noPlansAvailable}</div>
          <div style={styles.emptyText}>{t.noFilesInFolder}</div>
          <button
            style={{
              ...styles.refreshButton,
              ...(syncing ? styles.refreshButtonDisabled : {})
            }}
            onClick={handleSync}
            disabled={syncing}
          >
            <RefreshCw size={16} style={syncing ? { animation: 'spin 1s linear infinite' } : {}} />
            {syncing ? t.syncing : t.refresh}
          </button>
        </div>
      )}

      {/* Empty Folder State */}
      {!loading && !error && plans.length === 0 && syncStatus?.synced && (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            <Folder size={64} style={{ color: '#94a3b8', opacity: 0.5 }} />
          </div>
          <div style={styles.emptyTitle}>{t.emptyFolder}</div>
          <div style={styles.emptyText}>{t.noFilesHere}</div>
        </div>
      )}

      {!loading && !error && plans.length > 0 && (
        <div style={styles.list}>
          {plans.map((item) => (
            <div
              key={item.id}
              style={styles.card}
              onClick={() => handleItemClick(item)}
            >
              {getFileIcon(item)}
              <div style={styles.cardDetails}>
                <div style={styles.cardName}>{item.name}</div>
                {!item.isFolder && (
                  <div style={styles.cardMeta}>
                    {item.size && <span>{formatFileSize(item.size)}</span>}
                    <span>{formatDate(item.lastModified)}</span>
                  </div>
                )}
              </div>
              <div style={styles.cardAction}>
                <ExternalLink size={20} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
